import { useEffect, useState } from "react";
import { ViewUserDocument } from "./ViewUserDocument";
import { EditUserDocument } from "./EditUserDocument";
import { UserDocument } from "../../models/UserDocument";
import { Modes } from "./types";
import { DeleteUserDocument } from "./DeleteUserDocument";
import { UserDocumentDialogComponent } from "./UserDocumentDialogComponent"
import { UserDocumentDialogEmbedComponent } from "./UserDocumentDialogEmbedComponent";

interface Props {
  open: boolean;
  onComplete?: (shouldClose: boolean) => void;
  onUpdateDoc?: (shouldClose: boolean) => void;
  onCancel?: () => void;
  document: UserDocument;
  documentSrc: string;
  includeOnlyDocumentTypeIds?: number[]
  documentId?: string | number
  isLoading?: boolean,
  error?: Error | null
  isError?: boolean
  isSuccess?: boolean,
  mode?: Modes,
}

export const UserDocumentDialog = ({
  open,
  onComplete,
  onUpdateDoc,
  onCancel,
  document,
  documentSrc,
  includeOnlyDocumentTypeIds,
  isLoading,
  isError,
  error,
  isSuccess,
  documentId,
  mode,
}: Props): JSX.Element => {
  const [currentMode, setCurrentMode] = useState<Modes | undefined>(mode);

  useEffect(() => {
    if (!mode) {
      setCurrentMode(document?.id ? Modes.edit : Modes.create);
    }
  }, [document]);

  const getModeContentTitle = (): JSX.Element | string => {
    if (!document || !currentMode) return <></>;

    switch (currentMode) {
      case Modes.view: {
        return document.version?.title || "Document";
      }
      case Modes.create:
        return "Upload a document";
      case Modes.edit:
        return "Edit document";
      case Modes.delete:
        return "Delete document";
      case Modes.versions:
        if (!document?.version?.title) {
          return "Versions of file";
        }
        return `Versions of file "${document?.version?.title}"`;
    }
  };

  const getModeContent = (): JSX.Element => {
    if (!document || !currentMode) return <></>;

    switch (currentMode) {
      case Modes.view:
        return <ViewUserDocument
          document={document}
          documentSrc={documentSrc}
          setMode={setCurrentMode}
        />
      case Modes.create:
        return <EditUserDocument
          onCancel={() => onCancel?.()}
          onUpdateDoc={(shouldClose: boolean) => onComplete?.(shouldClose)}
          includeOnlyDocumentTypeIds={includeOnlyDocumentTypeIds}
          setMode={setCurrentMode}
          documentId={documentId}
          isError={isError}
          isLoading={isLoading}
          isSuccessful={isSuccess}
          error={error || undefined}
          document={undefined}
          documentSrc={undefined}
        />
      case Modes.edit:
        return <EditUserDocument
          document={document}
          documentSrc={documentSrc}
          onCancel={() => onCancel?.()}
          onUpdateDoc={(shouldClose: boolean) => onUpdateDoc?.(shouldClose)}
          includeOnlyDocumentTypeIds={includeOnlyDocumentTypeIds}
          setMode={setCurrentMode}
          documentId={documentId}
          isError={isError}
          isLoading={isLoading}
          isSuccessful={isSuccess}
          error={error || undefined}
        />
      case Modes.delete:
        return <DeleteUserDocument
          documentId={documentId}
          document={document}
          documentSrc={documentSrc}
          currentMode={currentMode}
          onComplete={(shouldClose: boolean) => onComplete?.(shouldClose)}
          onCancel={onCancel}
          setMode={setCurrentMode}
        />
    }

    return <></>
  }

  if (currentMode === Modes.view && document?.version?.cloudUrl) {
    return <UserDocumentDialogEmbedComponent
      isOpen={open}
      onCancel={onCancel || (() => { return undefined })}
      setMode={setCurrentMode}
      cloudUrl={document?.version?.cloudUrl}
    />
  }


  return <UserDocumentDialogComponent
    mode={currentMode || Modes.edit}
    isOpen={open}
    onCancel={onCancel || (() => { return undefined })}
    getModeContent={getModeContent}
    getModeContentTitle={getModeContentTitle}
  />
}